import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';

const LanuakumAierProfile = () => {
  const person = {
    id: "lanuakum-aier",
    name: "Lanuakum Aier",
    honorific: "Mr",
    title: "Deputy Executive Director & Operations Manager",
    affiliation: "The Highland Institute, Kohima, Nagaland",
    image: "https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/bbgi7sgp_Lanuakum%20Aier.jpg",
    email: "lanu@highlandinstitute.org",
    category: "Senior Staff"
  };

  const SectionDivider = () => (
    <div className="my-12 flex items-center justify-center">
      <div className="h-px w-24 bg-stone-300"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="lanuakum-aier-profile">
      {/* Breadcrumb Navigation */}
      <div className="bg-stone-50 border-b border-stone-200">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center text-sm text-stone-600">
            <Link to="/" className="hover:text-green-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/people" className="hover:text-green-600 transition-colors">People</Link>
            <span className="mx-2">/</span>
            <span className="text-stone-900">{person.name}</span>
          </nav>
        </div>
      </div>

      {/* Identity Header */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-16 border-b border-stone-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-lg overflow-hidden bg-stone-200 shadow-lg">
                  <img 
                    src={person.image} 
                    alt={person.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Identity Information */}
              <div className="flex-1">
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium uppercase tracking-wider rounded-full">
                    {person.category}
                  </span>
                </div>
                <h1 className="text-4xl font-serif font-bold text-stone-900 mb-2">
                  <span className="text-stone-600">{person.honorific} </span>
                  {person.name}
                </h1>
                <p className="text-xl text-stone-700 font-medium mb-4">
                  {person.title}
                </p>
                <p className="text-stone-600 leading-relaxed">
                  {person.affiliation}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">

          {/* Research and Professional Biography */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Research and Professional Biography
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                Lanuakum Aier is a research administrator and institutional development practitioner whose work focuses on the infrastructures, relationships, and field realities that sustain collaborative research in Highland Asia. His interests centre on how locally grounded institutions can support ethical scholarship, interdisciplinary collaboration, and long-term knowledge production in geographically remote and politically complex regions.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                He currently serves as Deputy Executive Director and Operations Manager at The Highland Institute in Kohima, Nagaland, where he has worked since 2018. His responsibilities include institutional operations, project management, partnership development, field coordination, and postgraduate training support across initiatives involving universities, NGOs, independent scholars, artists, and community organisations.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                Working across administration, field logistics, and collaborative research projects in Northeast India has shaped his interest in the operational and ethical dimensions of research practice in frontier regions. Much of Aier's work involves navigating the challenges of conducting long-term research in areas marked by infrastructural limitations, difficult terrain, and complex relationships between local communities, state institutions, and international academic networks.
              </p>
              <p className="text-stone-700 leading-relaxed">
                At The Highland Institute, Aier has contributed to research and training platforms connected to Indigenous knowledge systems, environmental humanities, oral history, governance, public health, and documentary-based research. Although not situated within a single disciplinary tradition, his work reflects an interdisciplinary engagement with collaborative research ecologies and community-engaged scholarship. He is particularly interested in how research institutions in peripheral regions can create sustainable knowledge infrastructures that remain accountable to local histories, lived realities, and emerging generations of scholars.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Research Foci & Areas of Work */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Research Foci & Areas of Work
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-6">
                Lanuakum Aier's work engages with the institutional and operational dimensions of interdisciplinary research in Highland Asia, particularly the relationship between research infrastructures, field practice, and community engagement in Northeast India.
              </p>
              <p className="text-stone-700 font-medium mb-4">Key areas of work include:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Research administration and institutional development</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Collaborative research coordination and partnership building</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Field logistics and ethical research support</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Postgraduate research training and capacity development</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Indigenous knowledge documentation</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Environmental humanities and sustainability projects</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Editorial coordination and publication support</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Oral history and documentary-based research</span>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Research governance and organisational systems</span>
                </li>
              </ul>
            </div>
          </section>

          <SectionDivider />

          {/* Highland Institute Projects */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Highland Institute Projects
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                At The Highland Institute, Aier has contributed to interdisciplinary projects related to environmental humanities, governance, Indigenous ecological knowledge, oral traditions, public health, and participatory documentary initiatives.
              </p>
              <p className="text-stone-700 leading-relaxed mb-4">
                His work has involved collaborations with institutions and scholars in India, Norway, Brazil, and the United Kingdom through initiatives such as <strong>"Ekologos"</strong>, a global programme that brings together students, researchers, and institutions across the environmental sciences and humanities to address urgent ecological challenges. He has also contributed to community-based health and documentary projects, as well as collaborative research programmes focused on Indigenous knowledge systems and regional histories.
              </p>
              <p className="text-stone-700 leading-relaxed">
                He has also supported the Institute's postgraduate research training initiatives, including the Postgraduate Certificate in Research (PGCertR), Summer School programmes, and field-based workshops for emerging researchers in Northeast India. His contributions include programme coordination, institutional planning, logistical management, partnership facilitation, editorial and publication support, and the ongoing development of The Highland Institute Wiki, a collaborative knowledge platform for projects, communities, languages, material culture, environmental knowledge, archives, and related themes.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Selected Publications & Knowledge Outputs */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Selected Publications & Knowledge Outputs
            </h2>
            <div className="prose prose-stone max-w-none">
              <ul className="space-y-4">
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <span className="font-medium text-stone-900">2024</span> &mdash; 'How ready is the health care system in Northeast India for surgical delivery? A mixed-methods study on surgical capacity and need' in <em>PLOS ONE</em>, Vol. 19, Issue 6, Article e0287941.{' '}
                  <a
                    href="https://doi.org/10.1371/journal.pone.0287941"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-700 hover:text-green-800 underline break-all"
                  >
                    https://doi.org/10.1371/journal.pone.0287941
                  </a>
                </li>
                <li className="text-stone-700 leading-relaxed pl-4 border-l-2 border-green-600">
                  <span className="font-medium text-stone-900">2024</span> &mdash; 'Everyday Lives of Naga Women' in <em>Highlander</em>, Vol. 3, Issue 2, pp. 134–141.{' '}
                  <a
                    href="https://doi.org/10.5281/zenodo.11002325"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-700 hover:text-green-800 underline break-all"
                  >
                    https://doi.org/10.5281/zenodo.11002325
                  </a>
                </li>
              </ul>
            </div>
          </section>

          <SectionDivider />

          {/* Public Engagement, Teaching & Community Work */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Public Engagement, Teaching & Community Work
            </h2>
            <div className="prose prose-stone max-w-none">
              <p className="text-stone-700 leading-relaxed mb-4">
                Alongside his operational responsibilities, Aier has contributed to workshops, conferences, lectures, documentary initiatives, and collaborative training programmes involving scholars, students, filmmakers, artists, and community organisations.
              </p>
              <p className="text-stone-700 leading-relaxed">
                A significant part of his work involves supporting early-career researchers through programme coordination, institutional guidance, and field-based research support. In these roles, he helps strengthen locally rooted research cultures and collaborative scholarship in Highland Asia.
              </p>
            </div>
          </section>

          <SectionDivider />

          {/* Contact & Scholarly Infrastructure */}
          <section className="mb-16">
            <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b-2 border-stone-200">
              Contact & Scholarly Infrastructure
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Institutional Affiliation */}
              <div className="bg-stone-50 p-6 rounded-lg">
                <h3 className="font-semibold text-stone-900 mb-3">Institutional Affiliation</h3>
                <p className="text-stone-700 leading-relaxed text-sm">
                  The Highland Institute<br />
                  Meluri Road<br />
                  Kohima, Nagaland<br />
                  797001, India
                </p>
              </div>

              {/* Email */}
              <div className="bg-stone-50 p-6 rounded-lg">
                <h3 className="font-semibold text-stone-900 mb-3">Email</h3>
                <div className="flex items-center gap-3 text-stone-700">
                  <Mail className="w-5 h-5 text-green-600" />
                  <a
                    href={`mailto:${person.email}`}
                    className="hover:text-green-600 transition-colors break-all"
                    data-testid="lanu-email-link"
                  >
                    {person.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Professional Role */}
            <div className="mt-8">
              <h3 className="font-semibold text-stone-900 mb-4">Professional Role</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <span>Deputy Executive Director &amp; Operations Manager, The Highland Institute</span>
                </li>
              </ul>
            </div>

            {/* Scholarly Profiles */}
            <div className="mt-8">
              <h3 className="font-semibold text-stone-900 mb-4">Scholarly Profiles</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <a
                    href="https://orcid.org/0009-0001-9738-8392"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-600 transition-colors"
                    data-testid="lanu-orcid-link"
                  >
                    ORCID
                  </a>
                </li>
                <li className="flex items-start gap-3 text-stone-700">
                  <span className="text-green-600 mt-1">•</span>
                  <a
                    href="https://www.linkedin.com/in/lanuakum-aier-821360276"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-600 transition-colors"
                    data-testid="lanu-linkedin-link"
                  >
                    LinkedIn
                  </a>
                </li>
              </ul>
            </div>
          </section>

        </div>
      </div>

      {/* Footer Note */}
      <div className="bg-stone-50 border-t border-stone-200 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-stone-500 font-serif italic">
              This profile forms part of The Highland Institute's living scholarly archive, documenting research trajectories, collaborative commitments, and the intellectual work shaping the Institute's wider academic community.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanuakumAierProfile;
